/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Icon, IconSet, LinkText } from '@postscript/components';
import { MessageDetailsDrawer } from 'components/conversations/components/messageDetails/MessageDetailsDrawer';
import ShopRcsMessage from 'components/preview/ShopRcsMessage';
import parseMessageForHtml from 'components/responses/utils/parseMessageForHtml';
import parseMessagesForPreview from 'components/responses/utils/parseMessageForPreview';
import { TooltipPortal } from 'components/shopper/components/shared/TooltipPortal';
import { isVcardFileUrl } from 'components/vcards/VcardFileUrl';
import VcardPhoneMessage from 'components/vcards/VcardPhoneMessage';
import { SMS_SALES_VIEW_LOCAL_STORAGE_KEY } from 'constants/constants';
import {
  SHOPPER_AI_MESSAGE_DETAILS_PS_LAB,
  usePSLabs,
} from 'controllers/contexts/labsFeatures';
import { useShopperStatus } from 'hooks/useShopperStatus';
import { useState } from 'react';
import Lightbox from 'react-image-lightbox';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { isShopperSubtype, ViewOptions, VIEW_OPTIONS } from '../constants';
import { useView } from '../hooks/useView';
import {
  MediaFlexWrapper,
  MediaThreadPreview,
  MessageColumn,
  MessageFlexWrapper,
  StyledAdminInfo,
  StyledAdminInfoItem,
  StyledAvatar,
  StyledBodyText,
  StyledMessageBubble,
  StyledTooltip,
} from '../styles/message';
import { Message, Subscriber } from '../types';
import { getUserAvatar } from '../utils/roles';
import { isShopperAutomationMessage, isShopperMessage } from './messageGuards';
import MessageMetadata from './MessageMetadata';
import SenderMessageDrawer from './SenderMessageDrawer';

const StyledShopRcsMessage = styled(ShopRcsMessage)`
  margin: var(--spacing-2) 0 0;
`;

interface MessageMediaProps {
  message: Message;
  shopName?: string;
  onLightboxOpen: () => void;
  incoming: boolean;
}

const shouldObscureOutboundAvatar = (message?: Message) => {
  const storageKey = localStorage.getItem(SMS_SALES_VIEW_LOCAL_STORAGE_KEY);
  return (
    (!storageKey || storageKey === '' || storageKey === 'Merchant View') &&
    message?.type !== 'sender'
  );
};

const parseMessageText = (message: string) =>
  parseMessageForHtml(parseMessagesForPreview(message));

interface MessageProps {
  message: Message;
  shopName?: string;
  id: string;
  showTimestamp?: boolean;
  subscriber: Subscriber | null;
  truncate?: boolean;
  forceObscureAvatar?: boolean;
  isLastShopperAutomationMessage?: boolean;
}

interface MessageAvatarProps {
  message: Message;
  currentView: ViewOptions;
  shopName?: string;
  id: string;
  forceObscureAvatar?: boolean;
}

export const getIconAvatarConfig = (
  message: Message,
  forceObscureAvatar?: boolean,
) => {
  if (
    (message.direction === 'outgoing' && isShopperSubtype(message.subtype)) ||
    isShopperAutomationMessage(message)
  ) {
    return {
      messageType: message.type,
      incoming: false,
      icon: IconSet.Sparkles,
      iconColor: 'var(--color-message-text-outbound)',
    };
  }

  if (message.direction === 'incoming') {
    return {
      messageType: message.type,
      incoming: true,
      icon: IconSet.User,
      iconColor: 'var(--blue-5)',
    };
  }

  if (shouldObscureOutboundAvatar(message) || forceObscureAvatar) {
    return {
      messageType: 'system',
      incoming: false,
      icon: IconSet.Message,
      iconColor: 'var(--color-message-text-outbound)',
    };
  }
};

const MessageAvatar = ({
  message,
  currentView,
  shopName,
  id,
  forceObscureAvatar,
}: MessageAvatarProps) => {
  const iconAvatarConfig = getIconAvatarConfig(message, forceObscureAvatar);

  if (iconAvatarConfig) {
    return (
      <StyledAvatar
        messageType={iconAvatarConfig.messageType}
        incoming={iconAvatarConfig.incoming}
      >
        <Icon
          color={iconAvatarConfig.iconColor}
          component={iconAvatarConfig.icon}
          size={18}
        />
      </StyledAvatar>
    );
  }

  return getUserAvatar({ message, currentView, shopName, id });
};

const MessageMedia = ({
  message,
  shopName,
  onLightboxOpen,
  incoming,
}: MessageMediaProps) => {
  if (!message.media_url) return null;

  return (
    <MediaFlexWrapper incoming={incoming}>
      {isVcardFileUrl(message.media_url) ? (
        <VcardPhoneMessage messageContent={shopName} />
      ) : (
        <MediaThreadPreview
          data-testid="media-thread-preview"
          alt="mms-media"
          src={message.media_url}
          onClick={onLightboxOpen}
          incoming={incoming}
          hasText={!!message.text}
        />
      )}
    </MediaFlexWrapper>
  );
};

export const MessageContainer = ({
  message,
  shopName,
  id,
  subscriber,
  showTimestamp = true,
  truncate = false,
  forceObscureAvatar = false,
  isLastShopperAutomationMessage = false,
}: MessageProps): JSX.Element => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { view: currentView } = useView();
  const incoming = message.direction === 'incoming';
  const isScheduledMessage = message.scheduled;
  const isShopperAutomation = isShopperAutomationMessage(message);

  const location = useLocation();
  const isInbox = location.pathname.includes('/workspace/inbox');
  const isResponses = location.pathname.includes('/responses');
  const { hasShopperFeatureEnabled } = useShopperStatus();
  const { hasLabsFlag } = usePSLabs();
  const hasMessageDetails = hasLabsFlag(SHOPPER_AI_MESSAGE_DETAILS_PS_LAB);

  // Shopper Responses render as AI bubbles (Sparkles avatar) just like Sender
  // messages, so they get the same Message Details affordance. They only
  // support the new drawer, though — the legacy SenderMessageDrawer has no
  // Shopper equivalent — so their entry point is gated on the labs flag.
  const isShopper = isShopperMessage(message);
  const canOpenDetails =
    isShopperAutomation || (isShopper && hasMessageDetails);

  const handleMessageClick = () => {
    // AI bubbles (Sender + Shopper) open their details drawer on click: the new
    // MessageDetailsDrawer when the labs flag is on, the legacy
    // SenderMessageDrawer otherwise. The Details icon in MessageMetadata is an
    // additional, keyboard-accessible entry point for the new drawer.
    if (canOpenDetails) {
      setIsDrawerOpen(true);
    }
  };

  return (
    <>
      <div style={{ position: 'relative' }}>
        <MessageMedia
          message={message}
          shopName={shopName}
          onLightboxOpen={() => setIsLightboxOpen(true)}
          incoming={incoming}
        />

        <MessageColumn incoming={incoming} messageType={message.type}>
          {(!message.media_url || message.text || message.rcs_message) && (
            <MessageFlexWrapper incoming={incoming}>
              {message.rcs_message && (
                <StyledShopRcsMessage
                  rcsMessage={message.rcs_message}
                  carouselOverflowSize="var(--spacing-9)"
                />
              )}
              {!message.rcs_message && message.text && (
                <StyledMessageBubble
                  className="message-bubble"
                  messageType={message.type}
                  incoming={incoming}
                  isScheduledMessage={isScheduledMessage}
                  data-key-pendo={
                    isShopperAutomation && isLastShopperAutomationMessage
                      ? 'message-sender'
                      : ''
                  }
                  onClick={handleMessageClick}
                  style={{
                    cursor: canOpenDetails ? 'pointer' : 'default',
                  }}
                  $isSender={!!canOpenDetails}
                  $drawerActive={!!isDrawerOpen}
                  $isHovered={!!isHovered}
                  onMouseEnter={() => canOpenDetails && setIsHovered(true)}
                  onMouseLeave={() => canOpenDetails && setIsHovered(false)}
                >
                  <StyledBodyText
                    forwardedAs="span"
                    color="currentColor"
                    size={
                      message.type?.includes('convert_event')
                        ? 'x-small'
                        : 'small'
                    }
                    truncate={truncate}
                  >
                    {parseMessageText(message.text)}
                  </StyledBodyText>
                </StyledMessageBubble>
              )}
              <MessageAvatar
                message={message}
                currentView={currentView as ViewOptions}
                shopName={shopName}
                id={id}
                forceObscureAvatar={forceObscureAvatar}
              />
            </MessageFlexWrapper>
          )}
          <MessageMetadata
            message={message}
            showTimestamp={showTimestamp}
            subscriber={subscriber}
            onOpenDetails={
              canOpenDetails && hasMessageDetails
                ? () => setIsDrawerOpen(true)
                : undefined
            }
          />
        </MessageColumn>

        {message.triggered_keyword_id &&
          !isInbox &&
          !isResponses &&
          hasShopperFeatureEnabled && (
            <StyledAdminInfo incoming={incoming}>
              <TooltipPortal>
                <StyledTooltip
                  id="triggered-keyword-tooltip"
                  place="top"
                  size="small"
                >
                  Shopper can’t reply to messages that contain keywords. To
                  allow responses, you’ll need to delete the keyword.
                </StyledTooltip>
              </TooltipPortal>
              <StyledAdminInfoItem
                incoming={incoming}
                messageType="triggered_keyword"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <Icon
                  component={IconSet.Information}
                  size={20}
                  color="var(--color-message-text-outbound)"
                  data-tip
                  data-for="triggered-keyword-tooltip"
                />
                Response skipped due to keyword:{' '}
                <LinkText
                  href={`/keywords/${message?.triggered_keyword_id}`}
                  target="_blank"
                >
                  {message?.triggered_keyword}
                </LinkText>
              </StyledAdminInfoItem>
            </StyledAdminInfo>
          )}
        {currentView === VIEW_OPTIONS.AI_DEBUGGER && (
          <StyledAdminInfo incoming={incoming}>
            <StyledAdminInfoItem
              incoming={incoming}
              messageType={message.type}
              data-testid="message-type"
            >
              Type: {message.type ?? '-'}
            </StyledAdminInfoItem>
            <StyledAdminInfoItem
              incoming={incoming}
              messageType="type_id"
              data-testid="message-type-id"
            >
              Type ID: {message.type_id?.toString() ?? '-'}
            </StyledAdminInfoItem>
            <StyledAdminInfoItem
              incoming={incoming}
              messageType={message.subtype}
              data-testid="message-subtype"
            >
              Subtype: {message.subtype ?? '-'}
            </StyledAdminInfoItem>
          </StyledAdminInfo>
        )}
      </div>

      {/* Message Lightbox */}
      {isLightboxOpen && message.media_url && (
        <Lightbox
          mainSrc={message.media_url}
          onCloseRequest={() => setIsLightboxOpen(false)}
        />
      )}

      {hasMessageDetails ? (
        <MessageDetailsDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          sentMessageId={canOpenDetails ? message.sent_message_id : undefined}
          message={message}
          subscriber={subscriber}
        />
      ) : (
        <SenderMessageDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          subscriber={subscriber}
        />
      )}
    </>
  );
};

export default MessageContainer;
